// Customizable Area Start
import React from "react";
import { Box, Typography, styled, DialogContent, Avatar } from "@mui/material";
import FullscreenRoundedIcon from "@mui/icons-material/FullscreenRounded";
import FullscreenExitRoundedIcon from "@mui/icons-material/FullscreenExitRounded";
import CallEndRoundedIcon from "@mui/icons-material/CallEndRounded";
import CallRoundedIcon from "@mui/icons-material/CallRounded";
import IncomingCallController, {
  Props,
  configJSON,
} from "./IncomingCallController.web";
import CustomDialogWrapper from "../../../components/src/CustomDialogWrapper.web";
import ConditionalRenderer from "../../../components/src/ConditionalRenderer.web";
import CustomButton from "../../../components/src/CustomButton.web";
import CustomIconButton from "../../../components/src/CustomIconButton.web";
import MicLevelIcon from "../../../components/src/MicLevelIcon.web";
import { formatCallTimer } from "../../../components/src/utils";
import VideoLevelIcon from "../../../components/src/VideoLevelIcon.web";
// Customizable Area End

export default class IncomingCall extends IncomingCallController {
  constructor(props: Props) {
    super(props);
    // Customizable Area Start
    // Customizable Area End
  }

  // Customizable Area Start
  // Customizable Area End

  render() {
    // Customizable Area Start
    const {
      incomingCall,
      isFullScreen,
      callStatus,
      isCallStarted,
      callTimer,
      isVideoCall,
      callerInfo,
      isMuted,
      isVideoOff,
    } = this.state;

    const IncomingCallIcon = isVideoCall ? (
      <VideoLevelIcon isOff={false} />
    ) : (
      <CallRoundedIcon sx={{ fontSize: "16px" }} />
    );

    return (
      <>
        {!!incomingCall ? (
          <CustomDialogWrapper
            open={!!incomingCall}
            maxWidth={"sm"}
            fullScreen={isFullScreen}
          >
            <FullScreenIcon
              data-test-id="toggle-fullscreen"
              onClick={this.toggleFullScreen}
            >
              {isFullScreen ? (
                <FullscreenExitRoundedIcon data-test-id="exit-fullscreen" />
              ) : (
                <FullscreenRoundedIcon />
              )}
            </FullScreenIcon>
            <DialogContent sx={{ display: "flex", padding: "0", minWidth: "250px" }}>
              <ContentWrapper>
                <CallContentWrapper>
                  <CallAvatar src={callerInfo.avatar} alt={callerInfo.name} />
                  <UserName>{callerInfo.name}</UserName>
                  <CallStatus>
                    {isCallStarted ? formatCallTimer(callTimer) : callStatus}
                  </CallStatus>
                </CallContentWrapper>
                <VideoFrameWrapper
                  id="callContainer"
                  isVideoCall={isVideoCall}
                  isCallStarted={isCallStarted}
                />
                <CallControlsWrapper>
                  <ConditionalRenderer isVisible={!isCallStarted}>
                    <CustomButton
                      sx={{ paddingX: "22px" }}
                      customVariant="green"
                      data-test-id="accept-button"
                      startIcon={IncomingCallIcon}
                      onClick={this.acceptCall}
                    >
                      {configJSON.acceptText}
                    </CustomButton>
                  </ConditionalRenderer>
                  <ConditionalRenderer isVisible={isCallStarted}>
                    <CustomIconButton
                      customVariant="primary"
                      data-test-id="call-mic-btn"
                      onClick={this.toggleMic}
                    >
                      <MicLevelIcon muted={isMuted} />
                    </CustomIconButton>
                    <ConditionalRenderer isVisible={isVideoCall}>
                      <CustomIconButton
                        data-test-id="call-video-btn"
                        customVariant="primary"
                        onClick={this.toggleVideo}
                      >
                        <VideoLevelIcon isOff={isVideoOff} data-test-id="video-icon" />
                      </CustomIconButton>
                    </ConditionalRenderer>
                  </ConditionalRenderer>
                  <CustomIconButton
                    customVariant="error"
                    data-test-id="cancel-call"
                    onClick={this.handleCallEnd}
                  >
                    <CallEndRoundedIcon />
                  </CustomIconButton>
                </CallControlsWrapper>
              </ContentWrapper>
            </DialogContent>
          </CustomDialogWrapper>
        ) : null}
      </>
    );
    // Customizable Area End
  }
}

// Customizable Area Start
const ContentWrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  flexGrow: 1,
  overflow: "hidden",
});

const FullScreenIcon = styled("button")({
  position: "absolute",
  top: "16px",
  right: "16px",
  padding: "4px",
  borderRadius: "50%",
  backgroundColor: "transparent",
  outline: "none",
  border: "none",
  "&:hover": {
    backgroundColor: "rgba(0,0,0,0.04)",
  },
  "& svg": {
    color: "#0F172A",
  },
});

const CallAvatar = styled(Avatar)({
  height: "70px",
  width: "70px",
  marginBottom: "2px",
  "& img": {
    objectPosition: "top",
  },
  "@media (max-width: 414px)": {
    height: "55px",
    width: "55px",
  },
  "@media (max-width: 850px)": {
    height: "65px",
    width: "65px",
  },
});

const UserName = styled(Typography)({
  color: "#0F172A",
  fontWeight: 500,
  fontSize: "1rem",
  letterSpacing: "-0.15px",
  lineHeight: "19.5px",
});

const CallContentWrapper = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  gap: "3px",
  width: "100%",
});

const CallStatus = styled(Typography)({
  color: "#64748B",
  fontWeight: 500,
  fontSize: "0.875rem",
  lineHeight: "normal",
});

const VideoFrameWrapper = styled(Box, {
  shouldForwardProp: (prop: string) =>
    !["isVideoCall", "isCallStarted"].includes(prop),
})<{ isVideoCall: boolean; isCallStarted: boolean }>(
  ({ isVideoCall, isCallStarted }) => ({
    width: isVideoCall && isCallStarted ? "100%" : "0",
    display: isVideoCall && isCallStarted ? "block" : "none",
    overflow: "hidden",
    marginTop: "15px",
    borderRadius: "12px",
    transition: "all 0.3s ease-in-out",
    minHeight: "270px",
    flex: 1,
    aspectRatio: 16 / 9,
    "@media (max-width: 480px)": {
      aspectRatio: "4 / 3",
    },
  })
);

const CallControlsWrapper = styled(Box)({
  display: "flex",
  alignItems: "center",
  marginTop: "15px",
  justifyContent: "center",
  gap: "10px",
});
// Customizable Area End
